import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Edit2,
  Plug,
  RefreshCw,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { StatusPill } from '@/components/StatusPill';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection } from '@/components/settings/SettingsPage';
import {
  crmSyncService,
  CrmConnection,
  CrmProvider,
  CrmSyncEvent,
  ManualTokens,
  SyncTrigger,
} from '@/services/crm-sync.service';

// ---------------------------------------------------------------------------
// Provider metadata
// ---------------------------------------------------------------------------
interface ProviderMeta {
  label: string;
  description: string;
  color: string;      // brand fill for the provider tile
  defaultFields: string[];
}

const PROVIDERS: Record<CrmProvider, ProviderMeta> = {
  hubspot: {
    label: 'HubSpot',
    description: 'Connect to HubSpot CRM to sync contacts and lifecycle stages.',
    color: 'bg-orange-500 text-white',
    defaultFields: ['firstname', 'lastname', 'email', 'phone', 'lifecyclestage'],
  },
  salesforce: {
    label: 'Salesforce',
    description: 'Push leads to Salesforce as Lead objects.',
    color: 'bg-blue-600 text-white',
    defaultFields: ['FirstName', 'LastName', 'Email', 'Phone', 'Status', 'LeadSource'],
  },
  pipedrive: {
    label: 'Pipedrive',
    description: 'Sync contacts and deals to Pipedrive.',
    color: 'bg-green-600 text-white',
    defaultFields: ['name', 'email', 'phone', 'label'],
  },
  zoho: {
    label: 'Zoho CRM',
    description: 'Push leads to Zoho CRM as Lead records.',
    color: 'bg-red-600 text-white',
    defaultFields: ['First_Name', 'Last_Name', 'Email', 'Phone', 'Lead_Status'],
  },
};

const LOCAL_LEAD_FIELDS = ['profileName', 'phone', 'email', 'status', 'source', 'notes'];

const SYNC_TRIGGER_LABELS: Record<SyncTrigger, string> = {
  on_qualified: 'When lead is qualified',
  on_won: 'When lead is won',
  manual: 'Manual push only',
  realtime: 'Realtime (every update)',
};

// ---------------------------------------------------------------------------
// Wizard step types
// ---------------------------------------------------------------------------
type WizardStep = 'auth' | 'mapping' | 'trigger';

interface WizardState {
  provider: CrmProvider;
  step: WizardStep;
  tokens: ManualTokens;
  fieldMappings: Record<string, string>;
  syncTrigger: SyncTrigger;
  existingConnectionUid?: string;
  saving: boolean;
}

const WIZARD_STEPS: { step: WizardStep; label: string }[] = [
  { step: 'auth', label: 'Authentication' },
  { step: 'mapping', label: 'Field mapping' },
  { step: 'trigger', label: 'Sync trigger' },
];

function buildDefaultMappings(provider: CrmProvider): Record<string, string> {
  const defaults = PROVIDERS[provider].defaultFields;
  const result: Record<string, string> = {};
  LOCAL_LEAD_FIELDS.forEach((f, i) => {
    if (defaults[i]) result[f] = defaults[i];
  });
  return result;
}

// ---------------------------------------------------------------------------
// Event status helpers
// ---------------------------------------------------------------------------
function eventStatusVariant(s: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (s === 'succeeded') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'pending') return 'warning';
  return 'neutral';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CrmSyncSettings() {
  const [connections, setConnections] = useState<CrmConnection[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [events, setEvents] = useState<Record<string, CrmSyncEvent[]>>({});
  const [loadingEvents, setLoadingEvents] = useState<Record<string, boolean>>({});
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<CrmConnection | null>(null);

  const load = async () => {
    setLoadError(false);
    try {
      const data = await crmSyncService.listConnections();
      setConnections(data);
    } catch {
      setLoadError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const loadEvents = async (connectionUid: string) => {
    setLoadingEvents((s) => ({ ...s, [connectionUid]: true }));
    try {
      const data = await crmSyncService.getEvents(connectionUid);
      setEvents((s) => ({ ...s, [connectionUid]: data }));
    } catch {
      // the axios interceptor already toasted
    } finally {
      setLoadingEvents((s) => ({ ...s, [connectionUid]: false }));
    }
  };

  const openWizard = (provider: CrmProvider, existing?: CrmConnection) => {
    setWizard({
      provider,
      step: existing ? 'mapping' : 'auth',
      tokens: { access: '', refresh: '', instanceUrl: existing?.instanceUrl, portalId: existing?.portalId },
      fieldMappings: existing?.fieldMappings && Object.keys(existing.fieldMappings).length > 0
        ? existing.fieldMappings
        : buildDefaultMappings(provider),
      syncTrigger: existing?.syncTrigger ?? 'on_qualified',
      existingConnectionUid: existing?.connectionUid,
      saving: false,
    });
  };

  const saveWizard = async () => {
    if (!wizard) return;
    setWizard((w) => w ? { ...w, saving: true } : null);

    try {
      if (wizard.existingConnectionUid) {
        await crmSyncService.updateConnection(wizard.existingConnectionUid, {
          fieldMappings: wizard.fieldMappings,
          syncTrigger: wizard.syncTrigger,
        });
        toast.success('Connection updated');
      } else {
        if (!wizard.tokens.access) {
          toast.error('Access token is required');
          setWizard((w) => w ? { ...w, saving: false } : null);
          return;
        }
        await crmSyncService.createConnection(wizard.provider, wizard.tokens);
        toast.success(`${PROVIDERS[wizard.provider].label} connected`);
      }
      setWizard(null);
      load();
    } catch (err: any) {
      if (!err?.response) toast.error('Failed to save connection');
      setWizard((w) => w ? { ...w, saving: false } : null);
    }
  };

  const handleDisconnect = async (conn: CrmConnection) => {
    try {
      await crmSyncService.removeConnection(conn.connectionUid);
      toast.success(`${PROVIDERS[conn.provider].label} disconnected`);
      setDisconnectTarget(null);
      load();
    } catch (err: any) {
      if (!err?.response) toast.error('Failed to disconnect');
    }
  };

  const handlePushTestLead = async (connectionUid: string) => {
    const leadUid = window.prompt('Enter a Lead UID to push:');
    if (!leadUid) return;
    try {
      const ev = await crmSyncService.pushLead(connectionUid, leadUid);
      toast.success(`Push ${ev.status} — external ID: ${ev.externalId ?? 'pending'}`);
      loadEvents(connectionUid);
    } catch (err: any) {
      if (!err?.response) toast.error('Push failed — see events below');
    }
  };

  return (
    <>
      <SettingsPage
        eyebrow="Channels"
        title="CRM sync"
        description="Send your qualified or won leads to an outside CRM such as HubSpot, Salesforce, Pipedrive or Zoho."
      >
        <div className="py-6">
          <div className="flex items-start gap-2.5 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm leading-relaxed text-text-secondary">
            <AlertTriangle size={15} className="mt-0.5 shrink-0 text-warning" />
            <p>
              Syncing with HubSpot, Salesforce, Pipedrive and Zoho is not part of this release, so new connections can't be added.
              Connections created earlier are listed below and can still be edited or disconnected.
            </p>
          </div>
        </div>

        <SettingsSection
          title="Connections"
          description="Each connection sends your leads to one outside CRM. Edit one to change which fields it maps and when it sends a lead."
        >
          {connections === null && loadError ? (
            <SettingsCard padded={false}>
              <LoadErrorState bare what="your CRM connections" onRetry={() => void load()} />
            </SettingsCard>
          ) : connections === null ? (
            <SettingsCard padded={false}>
              <div className="space-y-2 p-5">{[0, 1].map((i) => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
            </SettingsCard>
          ) : connections.length === 0 ? (
            <SettingsCard padded={false}>
              <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><Plug size={20} /></div>
                <div className="text-sm font-medium text-text-primary">No CRM connections yet</div>
                <p className="max-w-sm text-xs text-text-muted">
                  A CRM connection pushes your qualified or won leads to HubSpot, Salesforce, Pipedrive or Zoho automatically.
                  Adding new connections isn't available in this release.
                </p>
              </div>
            </SettingsCard>
          ) : (
            connections.map((conn) => (
              <ConnectionCard
                key={conn.connectionUid}
                conn={conn}
                events={events[conn.connectionUid]}
                loadingEvents={!!loadingEvents[conn.connectionUid]}
                onEdit={() => openWizard(conn.provider, conn)}
                onPush={() => handlePushTestLead(conn.connectionUid)}
                onLoadEvents={() => loadEvents(conn.connectionUid)}
                onDisconnect={() => setDisconnectTarget(conn)}
              />
            ))
          )}
        </SettingsSection>
      </SettingsPage>

      {/* Outside SettingsPage: its container query context also contains `position: fixed` descendants. */}
      {wizard && <ConnectionWizard wizard={wizard} setWizard={setWizard} onSave={saveWizard} />}

      {disconnectTarget && (
        <Modal open onClose={() => setDisconnectTarget(null)} title="Disconnect integration?">
          {/* New connections can't be added in this release, so "you can reconnect" would be untrue. */}
          <p className="text-sm text-text-secondary">
            This will stop all future syncs to <strong>{PROVIDERS[disconnectTarget.provider].label}</strong>.
            Historical sync events will be preserved. New connections can't be added in this release, so you won't be able to reconnect.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDisconnectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              iconLeft={<XCircle size={14} />}
              onClick={() => handleDisconnect(disconnectTarget)}
            >
              Disconnect
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Connection card
// ---------------------------------------------------------------------------
function DetailLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt className="shrink-0 text-xs text-text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-sm font-medium text-text-primary">{children}</dd>
    </div>
  );
}

interface ConnectionCardProps {
  conn: CrmConnection;
  events?: CrmSyncEvent[];
  loadingEvents: boolean;
  onEdit: () => void;
  onPush: () => void;
  onLoadEvents: () => void;
  onDisconnect: () => void;
}

function ConnectionCard({ conn, events, loadingEvents, onEdit, onPush, onLoadEvents, onDisconnect }: ConnectionCardProps) {
  const meta = PROVIDERS[conn.provider];
  const mappedCount = Object.keys(conn.fieldMappings ?? {}).length;
  const statusLabel = conn.syncStatus === 'error' ? 'Sync error' : conn.syncStatus === 'syncing' ? 'Syncing…' : 'Connected';
  const statusVariant = conn.syncStatus === 'error' ? 'danger' : conn.syncStatus === 'syncing' ? 'warning' : 'success';

  return (
    <SettingsCard padded={false}>
      <div className="flex items-start gap-4 px-5 pt-4">
        <div className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold', meta.color)}>
          {meta.label.charAt(0)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-text-primary">{meta.label}</span>
            <StatusPill status={statusLabel} variant={statusVariant} />
          </div>
          <div className="mt-0.5 text-xs text-text-muted">{meta.description}</div>
          {conn.lastSyncAt && <div className="mt-0.5 text-2xs text-text-muted">Last sync {relativeTime(conn.lastSyncAt)}</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 px-5 py-3">
        <Button size="sm" variant="secondary" iconLeft={<Edit2 size={13} />} onClick={onEdit}>
          Edit
        </Button>
        <Button size="sm" variant="ghost" iconLeft={<Send size={13} />} onClick={onPush}>
          Push test lead
        </Button>
        <Button size="sm" variant="ghost" iconLeft={<RefreshCw size={13} />} onClick={onLoadEvents} loading={loadingEvents}>
          Events
        </Button>
        <button
          type="button"
          onClick={onDisconnect}
          className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
          aria-label={`Disconnect ${meta.label}`}
          title="Disconnect"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <dl className="divide-y divide-border border-t border-border">
        <DetailLine label="Sync trigger">{SYNC_TRIGGER_LABELS[conn.syncTrigger]}</DetailLine>
        <DetailLine label="Field mappings">{mappedCount} field{mappedCount === 1 ? '' : 's'} configured</DetailLine>
        <DetailLine label="Provider ID">
          <span className="font-mono text-xs text-text-secondary">{conn.portalId ?? conn.instanceUrl ?? conn.connectionUid}</span>
        </DetailLine>
      </dl>

      {conn.syncStatus === 'error' && conn.lastSyncError && (
        <div className="flex items-start gap-2 border-t border-border bg-danger-soft px-5 py-3">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-danger" />
          <span className="min-w-0 break-words text-xs text-danger">{conn.lastSyncError}</span>
        </div>
      )}

      {events && (
        <div className="border-t border-border">
          <div className="bg-surface-alt/50 px-5 py-2.5 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
            Recent sync events
          </div>
          {events.length === 0 ? (
            <p className="px-5 py-4 text-xs text-text-muted">No sync events yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {events.slice(0, 10).map((ev) => (
                <li key={ev.eventUid} className="px-5 py-2.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <StatusPill status={ev.status} variant={eventStatusVariant(ev.status)} />
                    <StatusPill status={ev.action} variant="neutral" />
                    <span className="min-w-0 truncate font-mono text-xs text-text-muted" title={ev.leadUid}>{ev.leadUid}</span>
                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-2xs text-text-muted">
                      <Clock size={10} />
                      {relativeTime(ev.createdAt)}
                    </span>
                  </div>
                  {(ev.externalId || ev.error) && (
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
                      {ev.externalId && <span className="min-w-0 break-all font-mono text-text-secondary">→ {ev.externalId}</span>}
                      {ev.error && <span className="min-w-0 break-words text-danger">{ev.error}</span>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Connection wizard (modal)
// ---------------------------------------------------------------------------
function ConnectionWizard({ wizard, setWizard, onSave }: {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState | null>>;
  onSave: () => void;
}) {
  const meta = PROVIDERS[wizard.provider];
  const setToken = (patch: Partial<ManualTokens>) =>
    setWizard((w) => w ? { ...w, tokens: { ...w.tokens, ...patch } } : null);

  return (
    <Modal
      open
      onClose={() => !wizard.saving && setWizard(null)}
      title={`${meta.label} — ${wizard.existingConnectionUid ? 'Edit connection' : 'Connect'}`}
      widthClass="max-w-lg"
    >
      {/* Step tabs */}
      <div className="mb-5 flex gap-1 rounded-lg bg-surface-alt p-1">
        {WIZARD_STEPS.map(({ step, label }) => (
          <button
            key={step}
            type="button"
            onClick={() => setWizard((w) => w ? { ...w, step } : null)}
            aria-pressed={wizard.step === step}
            className={clsx(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              wizard.step === step
                ? 'bg-surface text-text-primary shadow-sm'
                : 'text-text-muted hover:text-text-primary',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Step: auth */}
      {wizard.step === 'auth' && (
        <div>
          {wizard.existingConnectionUid ? (
            <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success-soft px-4 py-3">
              <CheckCircle2 size={15} className="shrink-0 text-success" />
              <span className="text-sm text-success">Connected — tokens stored. To re-auth, disconnect and reconnect.</span>
            </div>
          ) : (
            <>
              <div className="mb-4 rounded-lg border border-info/30 bg-info-soft px-4 py-3">
                <div className="flex items-start gap-2">
                  <Plug size={14} className="mt-0.5 shrink-0 text-info" />
                  <div>
                    <div className="text-xs font-semibold text-info">OAuth coming soon</div>
                    <div className="mt-0.5 text-xs text-info/80">Paste tokens manually for now. OAuth will be a one-click setup in a future update.</div>
                  </div>
                </div>
              </div>

              <FormField label="Access token" required>
                <Input
                  type="password"
                  value={wizard.tokens.access}
                  onChange={(e) => setToken({ access: e.target.value })}
                  placeholder="Paste your access token"
                />
              </FormField>

              <FormField label="Refresh token (optional)">
                <Input
                  type="password"
                  value={wizard.tokens.refresh ?? ''}
                  onChange={(e) => setToken({ refresh: e.target.value })}
                  placeholder="Paste your refresh token"
                />
              </FormField>

              {wizard.provider === 'salesforce' && (
                <FormField label="Instance URL">
                  <Input
                    value={wizard.tokens.instanceUrl ?? ''}
                    onChange={(e) => setToken({ instanceUrl: e.target.value })}
                    placeholder="https://yourinstance.salesforce.com"
                  />
                </FormField>
              )}

              {wizard.provider === 'hubspot' && (
                <FormField label="Portal ID">
                  <Input
                    value={wizard.tokens.portalId ?? ''}
                    onChange={(e) => setToken({ portalId: e.target.value })}
                    placeholder="Your HubSpot portal ID"
                  />
                </FormField>
              )}
            </>
          )}
        </div>
      )}

      {/* Step: field mapping */}
      {wizard.step === 'mapping' && (
        <div className="space-y-3">
          <p className="text-xs text-text-secondary">
            Map each Slightor lead field to its corresponding {meta.label} field name.
          </p>
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {LOCAL_LEAD_FIELDS.map((localField) => (
              <div key={localField} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <div className="min-w-0 truncate rounded-md border border-border bg-surface-alt px-3 py-2 font-mono text-xs text-text-secondary">
                  {localField}
                </div>
                <span className="text-xs text-text-muted" aria-hidden>→</span>
                <Select
                  aria-label={`${meta.label} field for ${localField}`}
                  value={wizard.fieldMappings[localField] ?? ''}
                  onChange={(e) =>
                    setWizard((w) => {
                      if (!w) return null;
                      const fm = { ...w.fieldMappings };
                      if (e.target.value) fm[localField] = e.target.value;
                      else delete fm[localField];
                      return { ...w, fieldMappings: fm };
                    })
                  }
                >
                  <option value="">— skip —</option>
                  {meta.defaultFields.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step: sync trigger */}
      {wizard.step === 'trigger' && (
        <div className="space-y-3">
          <p className="text-xs text-text-secondary">Choose when leads should be pushed to {meta.label}.</p>
          <div className="space-y-2">
            {(Object.keys(SYNC_TRIGGER_LABELS) as SyncTrigger[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setWizard((w) => w ? { ...w, syncTrigger: t } : null)}
                aria-pressed={wizard.syncTrigger === t}
                className={clsx(
                  'w-full rounded-lg border px-4 py-3 text-left transition-colors',
                  wizard.syncTrigger === t
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-primary hover:border-primary/50',
                )}
              >
                <div className="text-sm font-semibold">{SYNC_TRIGGER_LABELS[t]}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={() => setWizard(null)} disabled={wizard.saving}>
          Cancel
        </Button>
        {wizard.step !== 'trigger' ? (
          <Button
            onClick={() =>
              setWizard((w) => {
                if (!w) return null;
                const steps: WizardStep[] = ['auth', 'mapping', 'trigger'];
                const idx = steps.indexOf(w.step);
                return { ...w, step: steps[Math.min(idx + 1, steps.length - 1)] };
              })
            }
          >
            Next
          </Button>
        ) : (
          <Button loading={wizard.saving} onClick={onSave}>
            {wizard.existingConnectionUid ? 'Save changes' : 'Connect'}
          </Button>
        )}
      </div>
    </Modal>
  );
}
